import { AlertCircle, CheckCircle, Circle } from 'lucide-react';
import { UseFormWatch } from 'react-hook-form';

import { ProviderFormData } from './types';

interface ProviderFormTabsProps {
  activeTab: 'basic' | 'contact' | 'details';
  onTabChange: (tab: 'basic' | 'contact' | 'details') => void;
  watch?: UseFormWatch<ProviderFormData>;
}

// Helper function to determine if a tab has content
const getTabCompletionState = (
  tabId: string,
  formData: ProviderFormData | undefined,
): 'complete' | 'partial' | 'empty' => {
  if (!formData) return 'empty';

  switch (tabId) {
    case 'basic': {
      const hasName = !!formData.name?.trim();
      const hasProviderType = !!formData.provider_type;

      if (hasName) return 'complete';
      if (hasProviderType) return 'partial';
      return 'empty';
    }
    case 'contact': {
      const hasAddress = !!formData.address?.trim();
      const hasHandles = formData.handles && formData.handles.length > 0;

      if (hasAddress || hasHandles) return 'complete';
      return 'empty';
    }
    case 'details': {
      const hasTreatment = !!formData.treatment?.trim();
      const hasDescription = !!formData.description?.trim();
      const hasPractitioners =
        formData.practitioner_names &&
        formData.practitioner_names.some((p) => p.name?.trim());
      const hasDate = !!formData.date_of_records_request;

      if (hasTreatment || hasDescription || hasPractitioners || hasDate) {
        return hasTreatment && hasDescription ? 'complete' : 'partial';
      }
      return 'empty';
    }
    default:
      return 'empty';
  }
};

const getTabIcon = (
  state: 'complete' | 'partial' | 'empty',
  isActive: boolean,
) => {
  if (state === 'complete') {
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  }
  if (state === 'partial') {
    return <AlertCircle className="h-4 w-4 text-amber-500" />;
  }
  return (
    <Circle
      className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}
    />
  );
};

export const ProviderFormTabs = ({
  activeTab,
  onTabChange,
  watch,
}: ProviderFormTabsProps) => {
  const formData = watch ? watch() : undefined;

  const tabs = [
    {
      id: 'basic' as const,
      label: 'Basic Info',
      description: 'Provider name, type, and general information',
      required: true,
    },
    {
      id: 'contact' as const,
      label: 'Contacts & Destinations',
      description: 'Contact handles and request destinations',
      required: false,
    },
    {
      id: 'details' as const,
      label: 'Treatment Details',
      description: 'Treatment information, practitioners, and dates',
      required: false,
    },
  ];

  // Calculate overall progress
  const completedTabs = tabs.filter(
    (tab) => getTabCompletionState(tab.id, formData) === 'complete',
  ).length;
  const totalTabs = tabs.length;

  return (
    <div className="space-y-4">
      {/* Progress Indicator */}
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <div className="text-xs font-medium">Progress:</div>
          <div className="flex items-center gap-1">
            <div className="text-blue-600 font-semibold">{completedTabs}</div>
            <div>of</div>
            <div>{totalTabs}</div>
            <div>sections complete</div>
          </div>
        </div>
        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${(completedTabs / totalTabs) * 100}%` }}
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 border-b border-gray-200">
        {tabs.map((tab) => {
          const completionState = getTabCompletionState(tab.id, formData);
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`group relative px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 min-w-0 ${
                isActive
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {getTabIcon(completionState, isActive)}
                <div className="flex flex-col items-start min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="truncate">{tab.label}</span>
                    {tab.required && (
                      <span className="text-red-500 text-xs">*</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate max-w-32 hidden sm:block">
                    {tab.description}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active Tab Description for Mobile */}
      <div className="sm:hidden">
        {tabs.map((tab) => {
          if (tab.id === activeTab) {
            return (
              <div key={tab.id} className="text-xs text-gray-600 px-1">
                {tab.description}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};
